/**
 * endoscopyPath.js — путь камеры виртуальной эндоскопии. [12.0.51]
 *
 * Чистая геометрия: без DOM и без движка. Проводка — core/viewportCamera.js
 * (setEndoscopyCamera), ползунки — ui/sidebarBindings.js.
 *
 * ПОВОД. В 12.0.50 эндоскопию «подключили», но картинка не менялась: 3D-окно
 * Cornerstone рисуется в ПАРАЛЛЕЛЬНОЙ проекции (VolumeViewport3D ставит
 * setParallelProjection(true)), а в ней расстояние камеры и угол обзора на
 * изображение не влияют. Замер на настоящем движке: 0…49% — тот же кадр, на 50%
 * камера совпадала с точкой фокуса (вырожденная камера), после 50% вид
 * переворачивался на противоположную сторону. Тест тогда проверял арифметику
 * позиции, а не картинку, и был зелёным.
 *
 * КАК ТЕПЕРЬ. Линия пути — луч взгляда через центр окна (через точку фокуса).
 * По ней ищется вход в объём и выход из него (пересечение с коробкой границ).
 * Позиция 1…100% ставит камеру на этот отрезок, камера смотрит вперёд по
 * лучу, проекция — ПЕРСПЕКТИВНАЯ (иначе изнутри ничего не видно). 0% — обычный
 * вид снаружи, ровно тот, что был до входа.
 *
 * EXPORTS: пересечениеСКоробкой, путьЭндоскопии, камераЭндоскопии, ВЗГЛЯД_ВПЕРЁД_ММ
 */

/** На сколько мм впереди камеры стоит точка фокуса (центр поворота мышью). */
export const ВЗГЛЯД_ВПЕРЁД_ММ = 10;

const длина = (v) => Math.hypot(v[0], v[1], v[2]);
const норм = (v) => { const д = длина(v); return д > 1e-9 ? [v[0] / д, v[1] / д, v[2] / д] : null; };

/**
 * Пересечение прямой «начало + t·направление» с коробкой [xmin,xmax,ymin,ymax,zmin,zmax].
 * t может быть отрицательным: прямая, а не луч.
 * @returns {{tВход:number, tВыход:number}|null}
 */
export function пересечениеСКоробкой(начало, направление, границы) {
  if (!начало || !направление || !границы || границы.length < 6) return null;
  let tMin = -Infinity, tMax = Infinity;
  for (let ось = 0; ось < 3; ось++) {
    const низ = границы[ось * 2], верх = границы[ось * 2 + 1];
    if (!Number.isFinite(низ) || !Number.isFinite(верх) || верх < низ) return null;
    const d = направление[ось], o = начало[ось];
    if (Math.abs(d) < 1e-12) {
      if (o < низ || o > верх) return null;      // прямая параллельна граням и идёт мимо
      continue;
    }
    let t1 = (низ - o) / d, t2 = (верх - o) / d;
    if (t1 > t2) [t1, t2] = [t2, t1];
    if (t1 > tMin) tMin = t1;
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax) return null;
  }
  return { tВход: tMin, tВыход: tMax };
}

/**
 * Путь по текущему виду: через точку фокуса, в сторону взгляда.
 * @param {{position:number[], focalPoint:number[], viewUp?:number[]}} камера
 * @param {number[]} границы — границы объёма в мировых координатах (мм)
 * @returns {{ok:true, вход:number[], выход:number[], длина:number, напр:number[], вверх:number[]}
 *          | {ok:false, причина:string}}
 */
export function путьЭндоскопии(камера, границы) {
  const p = камера?.position, f = камера?.focalPoint;
  if (!p || !f) return { ok: false, причина: 'нет текущего вида камеры' };
  const напр = норм([f[0] - p[0], f[1] - p[1], f[2] - p[2]]);
  if (!напр) return { ok: false, причина: 'камера стоит в точке фокуса — направление взгляда не определено' };
  const х = пересечениеСКоробкой(f, напр, границы);
  if (!х) return { ok: false, причина: 'линия взгляда через центр окна проходит мимо объёма — наведите центр окна на объём' };
  const длинаПути = х.tВыход - х.tВход;
  if (!(длинаПути > 1)) return { ok: false, причина: 'линия взгляда лишь задевает край объёма' };
  const вход = [0, 1, 2].map((i) => f[i] + напр[i] * х.tВход);
  const выход = [0, 1, 2].map((i) => f[i] + напр[i] * х.tВыход);
  // viewUp нельзя брать параллельным взгляду: камера бы вырождалась.
  const перпендикуляр = (v) => {
    const u = норм(v);
    if (!u) return null;
    const к = u[0] * напр[0] + u[1] * напр[1] + u[2] * напр[2];
    return норм([u[0] - к * напр[0], u[1] - к * напр[1], u[2] - к * напр[2]]);
  };
  const вверх = перпендикуляр(камера.viewUp || [0, 0, 1])
    || перпендикуляр(Math.abs(напр[2]) < 0.9 ? [0, 0, 1] : [0, 1, 0]);
  return { ok: true, вход, выход, длина: длинаПути, напр, вверх };
}

/**
 * Камера в точке пути.
 * @param {ReturnType<typeof путьЭндоскопии>} путь — с ok:true
 * @param {number} процент — 1…100 (0 сюда не приходит: это «снаружи»)
 * @param {number} угол — угол обзора, градусы (зажимается в 10…120)
 * @returns {{position:number[], focalPoint:number[], viewUp:number[], viewAngle:number}}
 */
export function камераЭндоскопии(путь, процент, угол = 90) {
  const доля = Math.max(0, Math.min(100, Number(процент) || 0)) / 100;
  // До самого выхода не доезжаем: камера в плоскости грани смотрела бы в пустоту.
  const запас = Math.min(ВЗГЛЯД_ВПЕРЁД_ММ, путь.длина * 0.1);
  const s = доля * Math.max(0, путь.длина - запас);
  const position = путь.вход.map((v, i) => v + путь.напр[i] * s);
  const focalPoint = position.map((v, i) => v + путь.напр[i] * ВЗГЛЯД_ВПЕРЁД_ММ);
  const viewAngle = Math.max(10, Math.min(120, Number(угол) || 90));
  return { position, focalPoint, viewUp: [...путь.вверх], viewAngle };
}
